/**
 * Gestion réactive de l'état pour le module Commerce.
 *
 * Ce module exporte les éléments suivants :
 * - isStepTwoActivated / isStepThreeActivated : indiquent l’activation des différentes étapes du processus.
 * - form_CommerceBody : données du formulaire de création/modification d’un commerce.
 * - file : fichier sélectionné (ex. image ou document).
 * - previewUrl : aperçu du fichier sélectionné.
 * - idToDelete : identifiant du commerce à supprimer.
 * - idToAddValue : identifiant utilisé pour ajouter une valeur liée au commerce.
 * - pageCommerce : numéro de page actuel pour la pagination des commerces.
 * - searchQueryCommerce : terme de recherche pour filtrer les commerces.
 *
 * Utilise les fonctions réactives de Vue 3 (ref et reactive).
 */
import { reactive, ref } from 'vue';
import { done, pending } from '@/data/common.mock';
import type {
  ICommerceBody,
  ICommerceBodyUpdate,
  IContact,
  ISingleOrganisation,
  ITranslationField,
} from '@/types/commerce.types';
import { useStorage } from '@vueuse/core';

export const isStepTwoActivated = ref(false);
export const isStepThreeActivated = ref(false);

export const form_CommerceBody = reactive<ICommerceBody>({
  designation: '',
  orderCode: '',
  currency: '',
  timezone: '',
  country: '',
  bannerDescription: { fr: '', en: '', nl: '' },
  metadata: {
    city: '',
    foundedYear: undefined,
    taglineCodes: { fr: [], en: [], nl: [] },
  },
});
export const form_CommerceContactBody = reactive<IContact>({
  type: '',
  content: '',
});

export const form_CommerceSlugBody = reactive<any>({
  slug: '',
  setPrimary: false,
  restaurantId: '62655039-0c78-41dd-9f1a-ea33ab548062',
});
export const form_CommerceBodyUpdate = reactive<ICommerceBodyUpdate>({
  designation: '',
  orderCode: '',
  currency: '',
  timezone: '',
  address: '',
  preparationTimeBuffer: null,
  logoId: null,
});

export const file = ref<File | null>(null);
export const previewUrl = ref<string | null>(null);
export const previewUrlOnboard = ref<string | null>(null);
export const idToDelete = ref<null | string>(null);
export const idToAddValue = ref<null | string>(
  '62655039-0c78-41dd-9f1a-ea33ab548062',
);
export const isImageUploadidOnboardingisSubmitting = ref(false);
export const isImageUploadidOnboarding = ref(false);
export const pageCommerce = ref(1);
export const searchQueryCommerce = ref('');
export const commerceId = useStorage<string | null>('commercesss', null);
export const isImageUploadid = ref(false);
export const isUpdatedFromConatct = ref(false);
export const isDeletedFromConatct = ref(false);
export const errorMessage = ref<string | null>(null);
export const fileInput = ref<HTMLInputElement | null>(null);
export const fileInputOnboarding = ref<HTMLInputElement | null>(null);
export const isAtcivatedWatch = ref(false);
export const isAtcivatedWatchContact = ref(false);
export const contactId = ref<any>(null);

// ── Params recherche commerces (/restaurants/search) ─────────────────────────
export const paginationCommerce = ref<boolean>(true);
export const pageSizeCommerce = ref<number>(20);
export const designationCommerce = ref<string | null>(null);
export const slugCommerce = ref<string | null>(null);
export const organizationIdCommerce = ref<string | null>(null);
export const unaffiliatedCommerce = ref<boolean | null>(null);
export const sortByCommerce = ref<'created' | 'name'>('created');
export const orderCommerce = ref<'ASC' | 'DESC'>('DESC');

export const lastCommerceId = useStorage<string | null>(
  'nxt_last_commerce_id',
  null,
);

export const pageSlug = ref<null | number>(1);
export const paginationSlug = ref<null | boolean>(false);
export const pageSizeSlug = ref<null | number>(20);
export const qSlug = ref<null | string>(null);
export const addAfterSlug = ref<null | string>(null);
export const addBeforeSlug = ref<null | string>(null);
export const sortBySlug = ref<null | string>(null);
export const orderSlug = ref<null | string>(null);
export const slugId = ref<null | string>(null);
export const slugStoreId = ref<null | string>(null);
export const slug = ref<null | string>(null);
export const text = ref<null | string>(null);
export const count = ref<null | number>(null);
export const textVailability = ref<null | string>(
  'Verifier la disponibilité du  slug',
);

export const storeCommerceId = ref<string | null>(null);
export const isUpdatedOrderOnboard = ref<boolean | null>(false);
export const storeCommerceSlug = ref<string | null>(null);

export const mediaId = ref<string | null>(null);
export const isLoadingFetaured = ref<boolean | null>(false);
export const isLoadingFetauredUptad = ref<boolean | null>(false);
export const isLoadingFetauredUpta = ref<boolean | null>(false);
export const idFetauredUpta = ref<string | null>(null);

export const formOnboardingImage = ref({
  restaurantId: '62655039-0c78-41dd-9f1a-ea33ab548062',
  role: 'onboarding_image' as
    'onboarding_image' | 'banner' | 'story' | 'product_highlight',
  displayOrder: '1',
  startDate: '',
  endDate: '',
  isActive: true,
  file: null as File | null,
  metadata: {
    title: '',
    description: '',
    slideDuration: '5000',
    skipAllowed: true,
  },
});

export const doneSteps = ref<Set<number>>(new Set());
export const SlugPrimaryValue = ref<any>();
export const SingleOrganisation = useStorage('single', []);
export const SingleCommerce = useStorage<ISingleOrganisation[]>(
  'SingleCommerce',
  [],
);
export const isSidebarCollapsed = ref(true);
export const isComercePage = ref<boolean>(false);
export const isCreateOrgPage = ref<boolean>(false);
export const isCreateCommercePage = ref<boolean>(false);
export const isEditOrgPage = ref<boolean>(false);
export const isEditCommercePage = ref<boolean>(false);
export const mainImageUrl = ref<string | null>(null);

export const fileImageUrl = ref<File | null>(null);
export const stepCreateOrganisation = ref<any[]>([
  {
    step: 1,
    title: 'Informations sur le client',
    statut: done,
    isActive: 'true',
  },
  { step: 2, title: 'Personne de contact', statut: pending, isActive: 'false' },
  {
    step: 3,
    title: 'Félicitations !',
    statut: pending,
    isActive: 'false',
  },
]);

export const stepCreateCommerce = ref<any[]>([
  {
    step: 1,
    title: 'Informations sur le commerce',
    statut: done,
    isActive: 'true',
  },
  { step: 2, title: 'Personne de contact', statut: pending, isActive: 'false' },
  { step: 3, title: 'Shop ID', statut: pending, isActive: 'false' },
  { step: 4, title: 'Boutique en ligne', statut: pending, isActive: 'false' },
  { step: 5, title: 'Félicitations !', statut: pending, isActive: 'false' },
]);

export const vatData = ref<any>(null);
export const stepOrg = ref<number>(1);

export const stepTitle = ref<string>('Informations sur la société');

export const stepCommerce = ref<number>(1);
export const createCommerceisPending = ref(false);
export const commerceIdHorraire = ref<any>(null);

export const stepTitleCommerce = ref<string>('Informations sur la société');
export const form = reactive({
  designation: '',
  activite: '',
  activiteAutre: '',
  activityId: '',
  timezone: '',
  adress: '',
});

export const hasSlug = ref<any>(false);
